import React, { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { isGroup, isLine, LineSchema, ReservationSchema, SaleOrdersFormTyping } from "../../types";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseDatepicker from "@reusables/BaseDatepicker";
import ReservationRow, { ProductEntry } from "./ReservationRow";

/**
 * ReservationsSection component for managing product reservations in sale orders.
 *
 * This component allows users to:
 * - Reserve specific quantities of products from the order
 * - Set an expiration date for reservations
 * - Toggle reservations for individual products or all products at once
 * - Track reservation quantities with history preservation
 *
 * The component aggregates products from order lines (including grouped lines)
 * and provides a UI to manage reservations with quantity validation and history.
 *
 * @returns {JSX.Element | null} The reservations section UI or null if no products exist
 */
export default function ReservationsSection() {
    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();

    const lines = useWatch({ control, name: "lines" });
    const reservations = useWatch({ control, name: "reservations" }) || [];
    const expirationDate = useWatch({ control, name: "reservations_expiration_date" });

    /**
     * Ref to store quantity history for products.
     * Preserves the last reserved quantity when a reservation is removed,
     * allowing users to restore previous values when re-enabling reservations.
     */
    const quantityHistoryRef = useRef<Record<number, number>>({});

    /**
     * Aggregates unique products from order lines and calculates total quantities.
     * Processes both individual lines and grouped lines, summing quantities
     * for products that appear multiple times.
     *
     * @returns {ProductEntry[]} Array of unique products with aggregated quantities
     */
    const uniqueProducts = useMemo(() => {
        const productsMap = new Map<number, ProductEntry>();

        const processLine = (line: LineSchema) => {
            const product = line.product;
            const quantity = line.quantity;
            if (!product) return;

            const existing = productsMap.get(product.id);
            if (existing) {
                existing.totalQuantity += quantity;
            } else {
                productsMap.set(product.id, { product, totalQuantity: quantity });
            }
        };

        lines.forEach(line => {
            if (isLine(line)) {
                processLine(line);
            } else if (isGroup(line)) {
                line.lines.forEach(l => processLine(l));
            }
        });

        return Array.from(productsMap.values());
    }, [lines]);

    /**
     * Creates a map of product IDs to their reservation data for quick lookup.
     *
     * @returns {Map<number, { product: any; quantity: number }>} Map of product ID to reservation
     */
    const reservationMap = useMemo(() => {
        const map = new Map<number, ReservationSchema>();
        reservations.forEach(reservation => {
            map.set(reservation.product.id, reservation);
        });
        return map;
    }, [reservations]);

    /**
     * Filters products that can be reserved (have quantity > 0).
     *
     * @returns {ProductEntry[]} Array of products that can be reserved
     */
    const reservableProducts = useMemo(
        () => uniqueProducts.filter(entry => entry.totalQuantity > 0),
        [uniqueProducts]
    );

    /**
     * Updates quantity history whenever reservations change.
     * Stores the current reservation quantity for each product to enable
     * restoration when reservations are toggled off and back on.
     */
    useEffect(() => {
        reservations.forEach(reservation => {
            quantityHistoryRef.current[reservation.product.id] = reservation.quantity;
        });
    }, [reservations]);

    /**
     * Gets the quantity to use when restoring a reservation for a product.
     * Priority: existing reservation > stored history > total quantity.
     *
     * @param {ProductEntry} entry - The product entry to get restored quantity for
     * @returns {number} The quantity to use for restoration
     */
    const getRestoredQuantity = (entry: ProductEntry): number => {
        const existing = reservationMap.get(entry.product.id);
        if (existing) {
            return existing.quantity;
        }
        const stored = quantityHistoryRef.current[entry.product.id];
        return stored && stored > 0 ? stored : entry.totalQuantity;
    };

    const removeReservation = (productId: number, preserveQuantity?: number) => {
        if (typeof preserveQuantity === "number") {
            quantityHistoryRef.current[productId] = preserveQuantity;
        } else {
            const existing = reservationMap.get(productId);
            if (existing) {
                quantityHistoryRef.current[productId] = existing.quantity;
            }
        }
        setValue(
            "reservations",
            reservations.filter(r => r.product.id !== productId)
        );
    };

    const upsertReservation = (entry: ProductEntry, quantity: number) => {
        const boundedQuantity = Math.min(Math.max(0, quantity), entry.totalQuantity);

        if (boundedQuantity === 0) {
            removeReservation(entry.product.id, 0);
            return;
        }

        quantityHistoryRef.current[entry.product.id] = boundedQuantity;

        const exists = reservations.some(r => r.product.id === entry.product.id);
        let nextReservations;

        if (exists) {
            nextReservations = reservations.map(r => {
                if (r.product.id === entry.product.id) {
                    return { ...r, quantity: boundedQuantity };
                }
                return r;
            });
        } else {
            nextReservations = [...reservations, { product: entry.product, quantity: boundedQuantity }];
        }

        setValue("reservations", nextReservations);
    };

    const handleRowToggle = (entry: ProductEntry, checked: boolean) => {
        if (checked) {
            const nextQuantity = getRestoredQuantity(entry);
            upsertReservation(entry, nextQuantity);
        } else {
            removeReservation(entry.product.id);
        }
    };

    const handleQuantityCommit = (entry: ProductEntry, quantity: number | null) => {
        if (quantity === null || quantity <= 0) {
            removeReservation(entry.product.id);
        } else {
            upsertReservation(entry, quantity);
        }
    };

    const handleToggleAll = (checked: boolean) => {
        if (!checked) {
            reservations.forEach(reservation => {
                quantityHistoryRef.current[reservation.product.id] = reservation.quantity;
            });
            setValue("reservations", []);
            return;
        }

        const fullReservations = reservableProducts.map(entry => {
            const quantity = getRestoredQuantity(entry);
            quantityHistoryRef.current[entry.product.id] = quantity;
            return {
                product: entry.product,
                quantity
            };
        });

        setValue("reservations", fullReservations);
    };

    const allReserved = reservableProducts.length > 0 &&
        reservableProducts.every(p => reservationMap.has(p.product.id));

    if (uniqueProducts.length === 0) return null;

    return (
        <div className="bg-[#F9F9F9] rounded-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 pb-2 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <BaseSwitch
                        checked={allReserved}
                        onChange={(_, checked) => handleToggleAll(checked)}
                        disabled={reservableProducts.length === 0}
                    />
                    <span className="text-lg font-medium text-accent">
                        Reserved products
                    </span>
                </div>
                <div className="w-[200px]">
                    <BaseDatepicker
                        placeholder="Expiration date"
                        value={expirationDate}
                        onChange={(date) => setValue("reservations_expiration_date", date)}
                        disablePast
                        inputSx={{
                            "& input": {
                                padding: "8px 16px"
                            }
                        }}
                    />
                </div>
            </div>

            {/* Rows */}
            <div className="pb-2">
                {uniqueProducts.map(entry => {
                    const reservation = reservationMap.get(entry.product.id);
                    const isReserved = !!reservation;
                    const reservedQuantity = reservation?.quantity ?? 0;

                    return (
                        <ReservationRow
                            key={entry.product.id}
                            entry={entry}
                            isReserved={isReserved}
                            reservedQuantity={reservedQuantity}
                            onToggle={(checked) => handleRowToggle(entry, checked)}
                            onQuantityCommit={(qty) => handleQuantityCommit(entry, qty)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
